import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../../../../context/AuthContext";
import { debug } from "../../../../../utils/debugLogger";
import { validateOAuthState } from "../../../../../utils/oauthState";
import { readPersistedWorkspace } from "../../../../../utils/workspacePersistence";
import { exchangeGscToken } from "../../../../../services/socialTokenService";
import { getGscOAuthRedirectUri } from "../config/gscEnv";
import { clearGscOAuthCustomerId, readGscOAuthCustomerId } from "../services/gscSession";
import { GSCProperty } from "../types/gsc.types";

interface UseGscConnectionResult {
  loading: boolean;
  error: string | null;
  /** Properties the grant can connect; empty once loaded means none are verified. */
  properties: GSCProperty[];
  /** Google account that granted access — shown so the user can spot a wrong login. */
  email: string;
  /** Customer the grant was started for; properties are connected to this customer. */
  customerId: string | undefined;
}

/**
 * Drive the Search Console OAuth callback: verify the CSRF state, hand the code to the
 * backend, and surface the properties it returns.
 *
 * The code is never exchanged in the browser — the backend holds the refresh token
 * until the user picks a property (see gscEnv.ts).
 */
export function useGscConnection(): UseGscConnectionResult {
  const location = useLocation();
  const { idToken } = useAuth();
  // Read the persisted workspace directly rather than through useCustomerWorkspace():
  // every OAuth callback renders outside MainLayout, where CustomerWorkspaceProvider
  // lives, so the hook would throw during render — a blank page with no error and no
  // request, since the throw beats the effect. LinkedInCallback documents the same
  // constraint. The context itself is only a wrapper over this value, so reading it
  // here is the same selection, not a weaker one.
  const persistedCustomerId = readPersistedWorkspace().id ?? undefined;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [properties, setProperties] = useState<GSCProperty[]>([]);
  const [email, setEmail] = useState("");
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);

  // The OAuth state is single-use and the authorization code may only be redeemed
  // once, so this must run exactly once per callback — not again when the auth token
  // resolves, and not twice under StrictMode's double-invoked effects.
  const exchangeStarted = useRef(false);

  const fail = useCallback((message: string, cause?: unknown) => {
    if (cause) debug.error("Search Console callback failed:", cause);
    setError(message);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (exchangeStarted.current) return;
    // Wait for the session to resolve rather than failing a callback that arrived
    // before AuthContext finished loading.
    if (!idToken) return;
    exchangeStarted.current = true;

    const params = new URLSearchParams(location.search);

    // Google reports a declined consent screen here, not as a missing code.
    const oauthError = params.get("error");
    if (oauthError) {
      clearGscOAuthCustomerId();
      fail(
        oauthError === "access_denied"
          ? "Google Search Console access was not granted."
          : `Google returned an error: ${oauthError}`
      );
      return;
    }

    if (!validateOAuthState("gsc", params.get("state"))) {
      clearGscOAuthCustomerId();
      fail("Security check failed (invalid OAuth state). Please try connecting again.");
      return;
    }

    const code = params.get("code");
    if (!code) {
      clearGscOAuthCustomerId();
      fail("Authorization code not found in URL.");
      return;
    }

    const targetCustomerId = readGscOAuthCustomerId() || persistedCustomerId;
    if (!targetCustomerId) {
      clearGscOAuthCustomerId();
      fail("No business profile selected. Please choose one and connect again.");
      return;
    }
    setCustomerId(targetCustomerId);

    exchangeGscToken(code, targetCustomerId, idToken, getGscOAuthRedirectUri())
      .then((response) => {
        setProperties(response.sites ?? []);
        setEmail(response.email ?? "");
        setLoading(false);
      })
      .catch((err: Error) => fail(err.message, err))
      // The grant now lives server-side; nothing further needs the pinned customer.
      .finally(clearGscOAuthCustomerId);
  }, [location.search, idToken, persistedCustomerId, fail]);

  return { loading, error, properties, email, customerId };
}
